/**
 * Type definitions for Document Assistant AI
 * Handles mortgage document classification, extraction, and processing
 *
 * Phase 1: 150+ document types with smart filtering (classification only)
 * Phase 2: Data extraction and enhanced filenames (future)
 */

import { DOCUMENT_TYPES } from '@/config/document-types';

/**
 * Loan document types - 150+ granular types for accurate classification
 * Auto-generated from document-types config to stay in sync
 */
export type LoanDocumentType = keyof typeof DOCUMENT_TYPES;

/**
 * Document classification result from AI
 */
export interface DocumentClassification {
  /** Classified document type */
  type: LoanDocumentType;

  /** Confidence score (0.0 - 1.0) */
  confidence: number;

  /** AI reasoning for classification */
  reasoning?: string;

  /** Whether this PDF contains multiple documents */
  isMultiDocument?: boolean;

  /** Page ranges if multi-document (e.g., [{start: 1, end: 3, type: 'income_w2'}]) */
  pageRanges?: Array<{
    start: number;
    end: number;
    type: LoanDocumentType;
    confidence: number;
  }>;
}

/**
 * Structured data extracted from W2
 * Phase 2 feature - defined now for type safety
 */
export interface W2Extraction {
  taxYear: number;
  employerName: string;
  employerEin: string;
  employeeName: string;
  employeeSsnLast4?: string;
  wagesTipsCompensation: number;  // Box 1
  federalIncomeTaxWithheld: number; // Box 2
  socialSecurityWages?: number;   // Box 3
  socialSecurityTaxWithheld?: number; // Box 4
  medicareWages?: number;         // Box 5
  medicareTaxWithheld?: number;   // Box 6
}

/**
 * Structured data extracted from paystub
 * Phase 2 feature - defined now for type safety
 */
export interface PaystubExtraction {
  payPeriodStart: string;
  payPeriodEnd: string;
  payDate: string;
  employerName: string;
  employeeName: string;
  grossPayCurrent: number;
  grossPayYtd: number;
  netPayCurrent: number;
  deductions?: Array<{
    name: string;
    amount: number;
  }>;
}

/**
 * Structured data extracted from bank statement
 * Phase 2 feature - defined now for type safety
 */
export interface BankStatementExtraction {
  accountNumberLast4: string;
  statementDate: string;
  statementPeriodStart: string;
  statementPeriodEnd: string;
  beginningBalance: number;
  endingBalance: number;
  accountHolder: string;
  bankName: string;
}

/**
 * Union type for all extraction types
 * Extensible as new document types are added
 */
export type DocumentExtraction =
  | W2Extraction
  | PaystubExtraction
  | BankStatementExtraction
  | Record<string, unknown>; // For future types

/**
 * Application document with classification metadata
 * Extends ai_documents table data
 */
export interface ApplicationDocument {
  id: string;
  encompassLoanId: string;
  title: string;
  fileName: string;
  /** Original upload filename (before classification renaming) */
  originalFilename?: string;
  fileSize: number;
  uploadedBy: string;
  uploadedAt: string;

  /** AI classification result */
  classification?: DocumentClassification;

  /** Extracted structured data (Phase 2) */
  extractedData?: DocumentExtraction;

  /** Processing status */
  status: 'uploaded' | 'uploading' | 'processing' | 'classified' | 'extracted' | 'error' | 'queued' | 'completed';

  /** Error message if processing failed */
  error?: string;

  /** Storage URL for download */
  storageUrl?: string;

  /** Queue ID for tracking processing status */
  queueId?: string;

  /** Live queue state from document_processing_queue */
  queueStatus?:
    | 'pending'
    | 'extracting'
    | 'extracted'
    | 'classifying'
    | 'classified'
    | 'splitting'
    | 'completed'
    | 'failed'
    | null;
  queueProgress?: number | null;
  queueError?: string | null;

  /** Investor-lock versioning (scoped to document_type === 'investor_lock') */
  version?: number;
  isCurrentVersion?: boolean;

  /** Parent document ID if this is a split child document */
  parentDocumentId?: string;

  /** Page range in parent document */
  pageRange?: string;

  /** Whether this document is a parent (has been split into children) */
  isParentDocument?: boolean;

  /** Processing timing stats (for parent documents) */
  processingStats?: {
    extractionTimeMs?: number;
    classificationTimeMs?: number;
    splitTimeMs?: number;
    totalTimeMs?: number;
    startedAt?: string;
    completedAt?: string;
    /** Number of characters extracted from the document */
    extractedCharacters?: number;
    /** Total pages in the original document */
    totalPages?: number;
    /** Number of child documents created from splitting */
    childCount?: number;
  };

  /** Raw metadata from database for additional fields like split_completed */
  metadata?: Record<string, unknown>;
}

/**
 * Document type display labels for UI
 * Auto-generated from document-types config
 */
export const DOCUMENT_TYPE_LABELS: Record<LoanDocumentType, string> = Object.fromEntries(
  Object.entries(DOCUMENT_TYPES).map(([key, config]) => [key, config.displayName])
) as Record<LoanDocumentType, string>;

/**
 * Document type short names for filenames
 * Auto-generated from document-types config
 */
export const TYPE_SHORT_NAMES: Record<LoanDocumentType, string> = Object.fromEntries(
  Object.entries(DOCUMENT_TYPES).map(([key, config]) => [key, config.shortName])
) as Record<LoanDocumentType, string>;

/**
 * Document categories for filtering/grouping
 * Auto-generated from document-types config
 */
export const DOCUMENT_CATEGORIES = Object.entries(DOCUMENT_TYPES).reduce((acc, [key, config]) => {
  const category = config.category;
  if (!acc[category]) {
    acc[category] = [];
  }
  acc[category].push(key as LoanDocumentType);
  return acc;
}, {} as Record<string, LoanDocumentType[]>);

/**
 * Confidence threshold for automatic classification
 * Below this threshold, require manual review
 */
export const CONFIDENCE_THRESHOLD = {
  AUTO_ACCEPT: 0.90,  // ≥90%: Auto-accept classification
  REVIEW: 0.70,       // 70-89%: Show warning, allow acceptance
  REJECT: 0.70       // <70%: Require manual classification
} as const;
